import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useListBlogRevisions, useGetBlogRevision, useRestoreBlogRevision,
  getListBlogRevisionsQueryKey, getGetBlogRevisionQueryKey, getListAdminBlogPostsQueryKey, getListBlogPostsQueryKey,
  type BlogPost,
} from '@workspace/api-client-react';

const button = 'border border-border px-3 py-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed';

export function BlogRecovery({ id, baseRevision, csrfToken, blocked, onBusy, onRestored, onError }: {
  id: string; baseRevision: number; csrfToken: string; blocked: boolean;
  onBusy: (busy: boolean) => void; onRestored: (post: BlogPost) => void; onError: (error: unknown) => void;
}) {
  const client = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [revision, setRevision] = useState(0);
  const history = useListBlogRevisions(id, { query: { queryKey: getListBlogRevisionsQueryKey(id), enabled: expanded, staleTime: 0 } });
  const snapshot = useGetBlogRevision(id, revision, { query: { queryKey: getGetBlogRevisionQueryKey(id, revision), enabled: expanded && revision > 0, staleTime: 0 } });
  const restore = useRestoreBlogRevision({ request: { headers: { 'X-CSRF-Token': csrfToken } } });
  const selected = snapshot.data;

  const recover = async () => {
    if (!selected || blocked || restore.isPending) return;
    if (!window.confirm(`Restore revision ${selected.revision} as a new draft? This replaces the current saved entry and removes it from the public blog if published. The replaced version stays recoverable. Review and Publish separately to make it public.`)) return;
    onBusy(true);
    try {
      const post = await restore.mutateAsync({ id, data: { revision: selected.revision, baseRevision } });
      await Promise.all([
        client.invalidateQueries({ queryKey: getListBlogRevisionsQueryKey(id) }),
        client.invalidateQueries({ queryKey: getListAdminBlogPostsQueryKey() }),
        client.invalidateQueries({ queryKey: getListBlogPostsQueryKey() }),
      ]);
      onRestored(post);
      setRevision(0);
    } catch (error) { onError(error); }
    finally { onBusy(false); }
  };

  return (
    <section aria-label="Saved version recovery" className="mt-8 border-t border-border pt-6 space-y-4">
      <button type="button" className={button} disabled={restore.isPending} aria-expanded={expanded} onClick={() => setExpanded(!expanded)}>
        {expanded ? 'Hide saved versions' : 'Saved versions'}
      </button>
      {expanded && <>
        <p className="text-sm text-muted-foreground">
          Private recovery only. Restores create a new draft, never publish automatically, and keep the replaced version.
          Save or discard unsaved changes before restoring.
        </p>
        {history.isLoading && <p role="status">Loading saved versions…</p>}
        {history.isError && <p role="alert">Saved versions could not be loaded. <button type="button" className={button} onClick={() => history.refetch()}>Retry</button></p>}
        {history.data?.length === 0 && <p>No earlier versions yet. Versions are kept when saved content changes after recovery was enabled.</p>}
        {!!history.data?.length && <label className="block text-sm">
          Earlier saved version
          <select className="block mt-2 w-full border border-border bg-background p-2" value={revision} disabled={restore.isPending}
            onChange={event => { setRevision(Number(event.target.value)); restore.reset(); }}>
            <option value={0}>Choose a version to preview</option>
            {history.data.map(item => <option key={item.revision} value={item.revision}>
              Revision {item.revision} · {new Date(item.savedAt).toLocaleString()} · {item.status}{item.title ? ` · ${item.title}` : ''}
            </option>)}
          </select>
        </label>}
        {revision > 0 && snapshot.isLoading && <p role="status">Loading version…</p>}
        {revision > 0 && snapshot.isError && <p role="alert">Version could not be loaded. <button type="button" className={button} onClick={() => snapshot.refetch()}>Retry</button></p>}
        {revision > 0 && selected && <div className="space-y-3">
          <p className="text-sm">Revision {selected.revision} · {selected.status} · {selected.logDate} · {selected.location}</p>
          <p className="font-medium">{selected.title || 'Untitled entry'}</p>
          <pre className="whitespace-pre-wrap break-words max-h-80 overflow-auto border border-border p-4 text-sm font-mono" data-testid="revision-body">{selected.body}</pre>
          <button type="button" className={button} disabled={blocked || restore.isPending || snapshot.isFetching} onClick={recover} data-testid="button-restore-revision">
            {restore.isPending ? 'Restoring…' : `Restore revision ${selected.revision} as draft`}
          </button>
        </div>}
      </>}
    </section>
  );
}
